<template>
	<div class="p-4 md:p-6 flex-1 overflow-y-auto font-sans">
		<!-- ── Section Header ── -->
		<div
			class="flex items-center justify-between mb-6 pb-5 border-b border-primary-100 dark:border-dark-600">
			<div class="flex items-center gap-3">
				<div
					class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
					<svg
						class="w-5 h-5 text-primary-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
					</svg>
				</div>
				<div>
					<h1
						class="font-display text-xl md:text-2xl font-extrabold tracking-tight text-dark-700 dark:text-white">
						Event Management
					</h1>
					<p class="text-[13px] text-muted dark:text-muted-light mt-0.5">
						Analytics & insights
					</p>
				</div>
			</div>
		</div>

		<Breadcrumbs />

		<!-- ── Loading ── -->
		<div
			v-if="isLoading"
			class="flex flex-col items-center justify-center h-64 gap-4">
			<div
				class="w-12 h-12 rounded-full border-3 border-primary-100 dark:border-dark-500 border-t-primary-400 animate-spin" />
			<p class="text-[13px] text-muted dark:text-muted-light">
				Loading event data...
			</p>
		</div>

		<!-- ── Error ── -->
		<div
			v-else-if="error"
			class="p-5 rounded-card bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 mb-4">
			<p class="text-[13px] font-semibold text-red-600 dark:text-red-400 mb-3">
				{{ error }}
			</p>
			<button
				@click="refetch"
				class="px-4 py-2 rounded-xl text-[13px] font-bold bg-primary-400 text-white hover:bg-primary-600 transition-colors">
				Try Again
			</button>
		</div>

		<!-- ── Main content ── -->
		<div v-else-if="hasEventData" class="space-y-5">
			<!-- ── Row 1: Description + RSVP ── -->
			<div class="grid grid-cols-1 lg:grid-cols-3 gap-5">
				<!-- Description + meta -->
				<div
					class="lg:col-span-2 bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-6">
					<!-- title -->
					<p class ="text-[15px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-3">
						{{ currentEvent.title }}
					</p>
					<p
						class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-4">
						About This Events
					</p>

					<p
						class="text-[14px] text-dark-400 dark:text-white/70 leading-relaxed mb-5">
						{{ currentEvent.description }}
					</p>

					<!-- Meta row -->
					<div class="flex flex-wrap items-center gap-4 mb-5">
						<div
							class="flex items-center gap-2 text-[13px] text-muted dark:text-muted-light">
							<div
								class="w-7 h-7 rounded-lg bg-primary-50 dark:bg-dark-600 flex items-center justify-center flex-shrink-0">
								<svg
									class="w-3.5 h-3.5 text-primary-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
								</svg>
							</div>
							{{ $formatDate(currentEvent.date) }}
						</div>
						<div
							class="flex items-center gap-2 text-[13px] text-muted dark:text-muted-light">
							<div
								class="w-7 h-7 rounded-lg bg-primary-50 dark:bg-dark-600 flex items-center justify-center flex-shrink-0">
								<svg
									class="w-3.5 h-3.5 text-primary-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
							</div>
							{{ currentEvent.start_time }}
						</div>
					</div>

					<!-- Category tags -->
					<div class="flex flex-wrap gap-2">
						<span
							v-for="category in currentEvent.categories"
							:key="category.ID"
							class="inline-flex px-3 py-1 rounded-full text-[12px] font-semibold bg-primary-50 dark:bg-primary-400/15 text-primary-600 dark:text-primary-300 border border-primary-100 dark:border-primary-400/20">
							{{ category.Name }}
						</span>
					</div>
				</div>

				<!-- RSVP / Booking card -->
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-6">
					<div class="flex items-center justify-between mb-4">
						<p
							class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light">
							RSVP
						</p>
						<span
							class="font-display text-[15px] font-extrabold text-primary-400">
							{{ ticketSalesPercentage }}%
						</span>
					</div>

					<p
						class="text-[12px] font-semibold text-center text-muted dark:text-muted-light mb-4">
						Booking
					</p>

					<!-- Circular progress -->
					<div class="flex justify-center mb-6">
						<div class="relative w-32 h-32">
							<svg class="w-full h-full -rotate-90" viewBox="0 0 36 36">
								<path
									d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
									fill="none"
									stroke="currentColor"
									stroke-width="3"
									class="text-primary-100 dark:text-dark-600" />
								<path
									d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
									fill="none"
									stroke="currentColor"
									stroke-width="3"
									class="text-primary-400 transition-all duration-700"
									:stroke-dasharray="`${ticketSalesPercentage}, 100`"
									stroke-linecap="round" />
							</svg>
							<div class="absolute inset-0 flex items-center justify-center">
								<span
									class="font-display text-[15px] font-extrabold text-primary-400">
									{{ ticketSalesPercentage }}%
								</span>
							</div>
						</div>
					</div>

					<!-- Booking stats -->
					<div class="grid grid-cols-2 gap-3">
						<div
							class="rounded-xl bg-primary-50 dark:bg-dark-600 p-3 text-center">
							<p class="text-[11px] text-muted dark:text-muted-light mb-1">
								Booked
							</p>
							<p
								class="font-display text-[18px] font-extrabold text-primary-400">
								{{ currentEvent.tickets_sold }}
							</p>
						</div>
						<div
							class="rounded-xl bg-primary-50 dark:bg-dark-600 p-3 text-center">
							<p class="text-[11px] text-muted dark:text-muted-light mb-1">
								Remaining
							</p>
							<p
								class="font-display text-[18px] font-extrabold text-dark-700 dark:text-white">
								{{ currentEvent.capacity - currentEvent.tickets_sold }}
							</p>
						</div>
					</div>
				</div>
			</div>

			<!-- ── Row 2: Chart ── -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600">
				<Statistics :chartData="chartData" timeRange="year" />
			</div>

			<!-- ── Row 3: Settings ── -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-6">
				<p
					class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-4">
					Event Settings
				</p>
				<SettingsPage
					:eventsId="eventId"
					:bookingOpen="bookingOpen"
					v-model:bookingOpen="bookingOpen"
					v-model:musicRecommandation="musicRecommendations" />
			</div>

			<!-- ── Row 4: Ticket analytics + Music ── -->
			<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
				<!-- Ticket analytics -->
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-6">
					<p
						class="font-display text-[15px] font-bold text-dark-700 dark:text-white mb-1">
						Ticket Analytics
					</p>
					<p class="text-[12px] text-muted dark:text-muted-light mb-5">
						Real-time ticket metrics and trends
					</p>

					<table class="w-full border-separate border-spacing-0">
						<thead>
							<tr>
								<th
									class="text-left text-[11px] font-bold uppercase tracking-widest pb-3 px-3 text-muted dark:text-muted-light border-b border-primary-100 dark:border-dark-600">
									Price Label
								</th>
								<th
									class="text-left text-[11px] font-bold uppercase tracking-widest pb-3 px-3 text-muted dark:text-muted-light border-b border-primary-100 dark:border-dark-600">
									Unit Price
								</th>
								<th
									class="text-left text-[11px] font-bold uppercase tracking-widest pb-3 px-3 text-muted dark:text-muted-light border-b border-primary-100 dark:border-dark-600">
									Sold
								</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="ticket in tickets"
								:key="ticket.ID"
								class="hover:bg-primary-50 dark:hover:bg-dark-600/50 transition-colors">
								<td
									class="px-3 py-3 text-[13px] text-dark-700 dark:text-white/90 border-b border-primary-50 dark:border-dark-600">
									{{ ticket.Type }}
								</td>
								<td
									class="px-3 py-3 text-[13px] text-dark-400 dark:text-white/70 border-b border-primary-50 dark:border-dark-600">
									{{ ticket.Amount }}
								</td>
								<td
									class="px-3 py-3 border-b border-primary-50 dark:border-dark-600">
									<span
										class="text-[13px] font-semibold text-primary-600 dark:text-primary-400">
										{{ ticket.Sold }}
									</span>
								</td>
							</tr>
							<tr v-if="!tickets.length">
								<td
									colspan="3"
									class="px-3 py-6 text-[13px] text-muted dark:text-muted-light text-center">
									No tickets available
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Recommended Music -->
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-6">
					<p
						class="font-display text-[15px] font-bold text-dark-700 dark:text-white mb-1">
						Recommended Music
					</p>
					<p class="text-[12px] text-muted dark:text-muted-light mb-5">
						Songs your attendees are requesting
					</p>

					<div v-if="displayedMusic.length">
						<div class="overflow-x-auto">
							<table class="w-full border-separate border-spacing-0">
								<thead>
									<tr>
										<th
											v-for="h in ['Title', 'Artist', 'Genres', 'BPM', 'Link']"
											:key="h"
											class="text-left text-[11px] font-bold uppercase tracking-widest pb-3 px-3 text-muted dark:text-muted-light border-b border-primary-100 dark:border-dark-600">
											{{ h }}
										</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="(song, idx) in displayedMusic"
										:key="song.Link || idx"
										class="hover:bg-primary-50 dark:hover:bg-dark-600/50 transition-colors">
										<td
											class="px-3 py-3 text-[13px] text-dark-700 dark:text-white/90 border-b border-primary-50 dark:border-dark-600">
											{{ song.Title }}
										</td>
										<td
											class="px-3 py-3 text-[13px] text-dark-400 dark:text-white/70 border-b border-primary-50 dark:border-dark-600">
											{{ song.Artist }}
										</td>
										<td
											class="px-3 py-3 text-[13px] text-dark-400 dark:text-white/70 border-b border-primary-50 dark:border-dark-600">
											{{ song.Genres?.join(", ") || "–" }}
										</td>
										<td
											class="px-3 py-3 text-[13px] text-dark-400 dark:text-white/70 border-b border-primary-50 dark:border-dark-600">
											{{ song.BPM || "–" }}
										</td>
										<td
											class="px-3 py-3 border-b border-primary-50 dark:border-dark-600">
											<a
												:href="song.Link"
												target="_blank"
												class="text-[12px] font-semibold text-primary-400 hover:text-primary-600 dark:hover:text-primary-300 transition-colors">
												Listen →
											</a>
										</td>
									</tr>
								</tbody>
							</table>
						</div>

						<div v-if="hasMore" class="text-center mt-4">
							<button
								@click="loadMore"
								:disabled="loadingMore"
								class="px-5 py-2 rounded-xl text-[13px] font-bold bg-primary-50 dark:bg-dark-600 text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-dark-500 hover:bg-primary-100 dark:hover:bg-dark-500 disabled:opacity-50 transition-colors">
								<span v-if="loadingMore">Loading...</span>
								<span v-else>View More</span>
							</button>
						</div>
					</div>

					<div
						v-else
						class="flex flex-col items-center justify-center h-32 gap-2">
						<div
							class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-dark-600 flex items-center justify-center">
							<svg
								class="w-5 h-5 text-muted dark:text-muted-light"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
							</svg>
						</div>
						<p class="text-[13px] text-muted dark:text-muted-light">
							No music recommendations yet.
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { apiDataService } from "@/services/apiDataService";
import { computed, ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { showToast } from "@/constants/toast";
import SettingsPage from "@/components/SettingsPage.vue";
import Statistics from "@/components/StatisticView.vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";

export default {
	name: "EventAnalytics",
	components: { SettingsPage, Statistics, Breadcrumbs },
	setup() {
		const route = useRoute();
		const store = useStore();
		const eventId = computed(() => route.params.eventId);
		const currentEvent = ref(null);
		const chartData = ref([]);
		const musicList = ref([]);
		const displayedCount = ref(5);
		const tickets = ref([]);
		const loadingMore = ref(false);

		const displayedMusic = computed(() => {
			const list = musicList.value;
			if (!Array.isArray(list)) return [];
			return list.slice(0, displayedCount.value);
		});
		const hasMore = computed(
			() => musicList.value.length > displayedCount.value,
		);

		const bookingOpen = computed({
			get: () => currentEvent.value?.bookable ?? false,
			set: (value) => {
				if (currentEvent.value) {
					currentEvent.value.bookable = value;
					makeEventBookable(value);
				}
			},
		});

		const musicRecommendations = computed({
			get: () => currentEvent.value?.music_recommendation ?? false,
			set: (value) => {
				if (currentEvent.value) {
					currentEvent.value.music_recommendation = value;
					enableMusicRecommandation(value);
				}
			},
		});

		const isLoading = computed(() => store.state.event.loading);
		const error = computed(() => store.state.event.error);

		const fetchEvent = async () => {
			try {
				const response = await apiDataService.getEvent(eventId.value);
				currentEvent.value = response.data;
				chartData.value = response.data.analytics?.[0]?.charts ?? [];
				tickets.value = response.data.tickets || [];
			} catch (err) {
				console.error("Error fetching event:", err);
			}
		};

		const makeEventBookable = async () => {
			try {
				const response = await apiDataService.makeEventBookable(
					{ bookable: bookingOpen.value },
					eventId.value,
				);
				showToast("success", ` ${response?.data.message}`);
			} catch (error) {
				console.error("Error updating booking state:", error);
			}
		};

		const getRecommendedMusic = async () => {
			try {
				const response = await apiDataService.getRecommendedSong(eventId.value);
				musicList.value = response.data;
			} catch (error) {
				console.error("Error getting recommended music:", error);
			}
		};

		const enableMusicRecommandation = async () => {
			try {
				const response = await apiDataService.enableMusicRecommendation(
					{ music_recommendation: musicRecommendations.value },
					eventId.value,
				);
				showToast("success", ` ${response?.data.message}`);
			} catch (error) {
				console.error("Error updating booking state:", error);
			}
		};

		const loadMore = () => {
			loadingMore.value = true;
			setTimeout(() => {
				displayedCount.value += 5;
				loadingMore.value = false;
			}, 500);
		};

		const ticketSalesPercentage = computed(() => {
			if (!currentEvent.value || !currentEvent.value.capacity) return 0;
			return Math.round(
				(currentEvent.value.tickets_sold / currentEvent.value.capacity) * 100,
			);
		});

		const hasEventData = computed(() => !!currentEvent.value);

		onMounted(() => {
			fetchEvent();
			getRecommendedMusic();
		});

		return {
			currentEvent,
			isLoading,
			fetchEvent,
			hasEventData,
			bookingOpen,
			error,
			ticketSalesPercentage,
			makeEventBookable,
			musicList,
			loadMore,
			displayedMusic,
			hasMore,
			eventId,
			chartData,
			tickets,
			musicRecommendations,
		};
	},
};
</script>
